'use no memo';

import type { QuizAssignedDataWithAttempts } from '@repo/api/quiz/quizService';
import type { QuizAttemptPublic } from '@repo/types/quiz';
import type { ColumnDef, RowSelectionState } from '@tanstack/react-table';

import { useGetAssignedQuizzes } from '@repo/api/quiz/useGetAssignedQuizzes';
import { format, isAfter, isValid, isWithinInterval, parseISO, startOfDay, endOfDay } from 'date-fns';
import { EditIcon, Trash2Icon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router';

import DeleteQuiz from '@/components/features/manage/manage-quizzes/DeleteQuiz';
import TanstackTable from '@/components/features/manage/tanstack-table/TanstackTable';
import { useTanstackTable } from '@/components/features/manage/tanstack-table/useTanstackTable';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { LoadingSwap } from '@/components/ui/loading-swap';
import { useQuizBuilderData, useQuizInitialization } from '@/hooks/quiz';
import { useTableRowSelection } from '@/hooks/table';

export default function ManageQuizzesPage() {
  const { removeQuizDraftData } = useQuizBuilderData();

  const [openDelete, setOpenDelete] = useState<boolean>(false);
  const quizInitialization = useQuizInitialization();

  const [activeQuiz, setActiveQuiz] = useState<QuizAssignedDataWithAttempts | undefined>(undefined);

  const [rowSelection, setRowsSelection] = useState<RowSelectionState>({});

  const { data = [], isPending } = useGetAssignedQuizzes();

  const navigateToBuilder = async () => {
    removeQuizDraftData();
    await quizInitialization.initializeQuiz();
  };

  const columns = useMemo<ColumnDef<QuizAssignedDataWithAttempts>[]>(
    () => [
      {
        id: 'select',
        header: ({ table }) => (
          <Checkbox
            checked={table.getIsAllPageRowsSelected() ? true : table.getIsSomePageRowsSelected() ? 'indeterminate' : false}
            onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
            aria-label="Select all"
          />
        ),
        cell: ({ row }) => <Checkbox checked={row.getIsSelected()} onCheckedChange={(value) => row.toggleSelected(!!value)} aria-label={'Select row'} />,
        enableSorting: false,
        enableHiding: false,
      },
      {
        meta: 'Nazwa',
        id: 'name',
        accessorKey: 'name',
        header: 'Nazwa',
      },
      {
        meta: 'Dostępność',
        id: 'availability',
        header: 'Dostępność',
        cell: ({ row }) => {
          const { available, deadline } = row.original;

          if (
            isWithinInterval(new Date(), {
              start: available ?? new Date('1000-01-01'),
              end: deadline ?? new Date('9999-12-31'),
            })
          )
            return <Badge variant={'success'}>Aktywny</Badge>;

          if (deadline && isAfter(new Date(), deadline)) return <Badge variant={'destructive'}>Ukończony</Badge>;

          return <Badge variant={'blue'}>Nie rozpoczęty</Badge>;
        },
        filterFn: (row, _collumnId, filterValue) => {
          const { available, deadline } = row.original;
          const now = new Date();

          const state = isWithinInterval(now, {
            start: available ?? new Date('1000-01-01'),
            end: deadline ?? new Date('9999-12-31'),
          })
            ? 'Aktywny'
            : deadline && isAfter(now, deadline)
              ? 'Ukończony'
              : 'Nie rozpoczęty';

          return filterValue.includes(state);
        },
      },
      {
        meta: 'Ilość odpowiedzi',
        id: 'anscnt',
        accessorKey: 'quizresps',
        header: 'Ilość odpowiedzi',
        cell: ({ getValue }) => {
          const type = getValue() as QuizAttemptPublic[];

          return type.length;
        },
      },
      {
        meta: 'Ostatnie zmiany',
        id: 'updatedAt',
        accessorKey: 'updatedAt',
        sortType: 'datetime',
        cell: ({ getValue }) => {
          const date = getValue();
          if (typeof date === 'string' && isValid(parseISO(date))) {
            return format(new Date(date), 'HH:mm dd.MM.yyyy');
          }
          return date;
        },
        header: 'Ostatnie zmiany',
        filterFn: (row, id, value) => {
          if (!value || (!value.start && !value.end)) return true;
          const dateStr = row.getValue(id) as string | null;
          if (!dateStr || !isValid(parseISO(dateStr))) return false;
          const date = startOfDay(parseISO(dateStr));
          const start = value.start ? startOfDay(new Date(value.start)) : null;
          const end = value.end ? endOfDay(new Date(value.end)) : null;
          if (start && end) {
            return date >= start && date <= end;
          }
          if (start) {
            return date >= start;
          }
          if (end) {
            return date <= end;
          }
          return true;
        },
      },
      {
        meta: 'Dostępny od',
        id: 'available',
        accessorKey: 'available',
        sortType: 'datetime',
        cell: ({ getValue }) => {
          const date = getValue();

          if (date == null) return <p>-</p>;

          if (typeof date === 'string' && isValid(parseISO(date))) {
            return format(new Date(date), 'HH:mm dd.MM.yyyy');
          }
          return date;
        },
        header: 'Dostępny od',
        filterFn: (row, id, value) => {
          if (!value || (!value.start && !value.end)) return true;
          const dateStr = row.getValue(id) as string | null;
          if (!dateStr || !isValid(parseISO(dateStr))) return false;
          const date = parseISO(dateStr);
          const start = value.start ? new Date(value.start) : null;
          const end = value.end ? new Date(value.end) : null;
          if (start && end) {
            return date >= start && date <= end;
          }
          if (start) {
            return date >= start;
          }
          if (end) {
            return date <= end;
          }
          return true;
        },
      },
      {
        meta: 'Dostępny do',
        id: 'deadline',
        accessorKey: 'deadline',
        sortType: 'datetime',
        cell: ({ getValue }) => {
          const date = getValue();

          if (date == null) return <p>-</p>;

          if (typeof date === 'string' && isValid(parseISO(date))) {
            return format(new Date(date), 'HH:mm dd.MM.yyyy');
          }
          return date;
        },
        header: 'Dostępny do',
        filterFn: (row, id, value) => {
          if (!value || (!value.start && !value.end)) return true;
          const dateStr = row.getValue(id) as string | null;
          if (!dateStr || !isValid(parseISO(dateStr))) return false;
          const date = parseISO(dateStr);
          const start = value.start ? new Date(value.start) : null;
          const end = value.end ? new Date(value.end) : null;
          if (start && end) {
            return date >= start && date <= end;
          }
          if (start) {
            return date >= start;
          }
          if (end) {
            return date <= end;
          }
          return true;
        },
      },
      {
        meta: 'Data utworzenia',
        id: 'createdAt',
        accessorKey: 'createdAt',
        sortType: 'datetime',
        cell: ({ getValue }) => {
          const date = getValue();
          if (typeof date === 'string' && isValid(parseISO(date))) {
            return format(new Date(date), 'HH:mm dd.MM.yyyy');
          }
          return date;
        },
        header: 'Data utworzenia',
        filterFn: (row, id, value) => {
          if (!value || (!value.start && !value.end)) return true;
          const dateStr = row.getValue(id) as string | null;
          if (!dateStr || !isValid(parseISO(dateStr))) return false;
          const date = startOfDay(parseISO(dateStr));
          const start = value.start ? startOfDay(new Date(value.start)) : null;
          const end = value.end ? endOfDay(new Date(value.end)) : null;
          if (start && end) {
            return date >= start && date <= end;
          }
          if (start) {
            return date >= start;
          }
          if (end) {
            return date <= end;
          }
          return true;
        },
      },
      {
        id: 'actions',
        enableHiding: false,
        enableSorting: false,
        cell: ({ row }) => {
          const rowOrigin = row.original;

          return (
            <div className={'flex w-full justify-end'}>
              <Button
                size={'sm'}
                variant={'ghost'}
                variantColor={'destructive'}
                onClick={() => {
                  setActiveQuiz(rowOrigin);
                  setOpenDelete(true);
                }}
              >
                <Trash2Icon />
              </Button>
              <Button size={'sm'} variant={'ghost'} variantColor={'default'} onClick={removeQuizDraftData} asChild>
                <Link to={`builder/${rowOrigin.id}/basic-info`}>
                  <EditIcon />
                </Link>
              </Button>
            </div>
          );
        },
      },
    ],
    []
  );

  const { table, setGlobalFilter } = useTanstackTable({
    name: 'quizzes',
    columns,
    data,
    getRowId: (row) => row.id,
    rowSelection: rowSelection,
    onRowSelectionChange: setRowsSelection,
    defaultColumnVisibility: {
      desc: false,
      createdAt: false,
      deadline: false,
      available: false,
    },
  });

  const selectedQuizzes = useMemo<QuizAssignedDataWithAttempts[]>(() => {
    const selectedIds = Object.keys(rowSelection);
    if (selectedIds.length === 0) return [];
    const idSet = new Set(selectedIds);
    return data.filter((quiz) => idSet.has(quiz.id));
  }, [rowSelection, data]);

  const { handleRowClick } = useTableRowSelection({
    table,
    setRowSelection: setRowsSelection,
  });

  return (
    <>
      <DeleteQuiz
        quizzes={activeQuiz ? [activeQuiz] : selectedQuizzes}
        open={openDelete}
        onOpenChange={(open) => {
          setOpenDelete(open);
          if (!open) setActiveQuiz(undefined);
        }}
      />
      <div className={'base-direction-col h-full'}>
        <div className={'viewport-height flex w-full flex-col gap-4'}>
          <TanstackTable
            table={table}
            label={'Quizy'}
            isLoading={isPending}
            onGlobalFilterChange={setGlobalFilter}
            filterBy={[
              {
                id: 'availability',
                title: 'Dostępność',
                type: 'faceted',
                options: [
                  {
                    label: 'Aktywny',
                    value: 'Aktywny',
                  },
                  {
                    label: 'Ukończony',
                    value: 'Ukończony',
                  },
                  {
                    label: 'Nie rozpoczęty',
                    value: 'Nie rozpoczęty',
                  },
                ],
              },
              {
                id: 'available',
                title: 'Dostępny od',
                type: 'dateRange',
              },
              {
                id: 'deadline',
                title: 'Dostępny do',
                type: 'dateRange',
              },
              {
                id: 'createdAt',
                title: 'Data utworzenia',
                type: 'dateRange',
              },
              {
                id: 'updatedAt',
                title: 'Ostatnie zmiany',
                type: 'dateRange',
              },
            ]}
            onRemoveSelectedRows={() => setOpenDelete(true)}
            onRowClick={handleRowClick}
            actionsComponent={
              <Button size={'md'} onClick={navigateToBuilder}>
                <LoadingSwap isLoading={quizInitialization.isPending}>Utwórz quiz</LoadingSwap>
              </Button>
            }
          />
        </div>
      </div>
    </>
  );
}
